import React from 'react';

import { Box, Button, Typography } from '@mui/material';

import { Field, Form } from '@hrlogics/react-components/form';

import { CompanyType, useGenerateRevenueShareReportMutation } from 'src/api';
import { DateRangeField, RangeShortcut } from 'src/components/finalForm';
import { useNotification } from 'src/hooks/app';
import { downloadFileByDownloadUrl } from 'src/utils/file';

import { FormField, getMaxDate, MIN_DATE } from './constants';
import { FormValues } from './types';
import { formValidationSchema } from './validation';

type RevenueShareReportGeneratingModalContentProps = {
  onCloseModal: () => void;
  switchClosability: (closability: boolean) => void;
  companyId?: string;
  companyType?: CompanyType;
};

export const RevenueShareReportGeneratingModalContent: React.FC<
  RevenueShareReportGeneratingModalContentProps
> = ({ onCloseModal, switchClosability, companyId, companyType }) => {
  const { createApolloContext } = useNotification();

  const [generateRevenueShareReport] = useGenerateRevenueShareReportMutation({
    context: createApolloContext,
  });

  const handleSubmit = React.useCallback(
    async (formValues: FormValues) => {
      const [startDate, endDate] = formValues.dateRange || [null, null];
      if (!startDate || !endDate) return;

      switchClosability(false);

      try {
        const { data } = await generateRevenueShareReport({
          variables: {
            startDate,
            endDate,
            companyId,
            companyType,
          },
        });

        if (data?.generateRevenueShareReport) {
          downloadFileByDownloadUrl(
            data.generateRevenueShareReport.downloadUrl,
            data.generateRevenueShareReport.filename,
          );
        }

        switchClosability(true);
        onCloseModal();
      } catch {
        switchClosability(true);
      }
    },
    [companyId, companyType, generateRevenueShareReport, onCloseModal, switchClosability],
  );

  return (
    <Form<FormValues>
      onSubmit={handleSubmit}
      initialValues={initialValues}
      subscription={{ submitting: true, valid: true }}
      validationSchema={formValidationSchema}
    >
      {({ submitting, valid }) => (
        <React.Fragment>
          <Typography variant="body1" color="text.secondary">
            A date range is required to generate the report. Please select both start and end dates.
          </Typography>

          <Box mt={2}>
            <Field
              name={FormField.dateRange}
              component={DateRangeField}
              label="Date Range"
              fullWidth
              required
              rangeShortcuts={rangeShortcuts}
              minDate={MIN_DATE}
              maxDate={getMaxDate()}
            />
          </Box>

          <Box display="flex" gap={2} mt={4} justifyContent="center">
            <Button
              variant="outlined"
              color="error"
              onClick={() => onCloseModal()}
              disabled={submitting}
            >
              Close
            </Button>

            <Button type="submit" variant="contained" disabled={!valid} loading={submitting}>
              Generate report
            </Button>
          </Box>
        </React.Fragment>
      )}
    </Form>
  );
};

const initialValues: FormValues = {
  [FormField.dateRange]: [null, null],
};

export const rangeShortcuts = [
  RangeShortcut.sixMonths,
  RangeShortcut.threeMonths,
  RangeShortcut.oneMonth,
  RangeShortcut.yearToDate,
] as const satisfies RangeShortcut[];
